import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowRight, CheckCircle, Users, Building2, TrendingUp,
  Star, BookOpen, Target, Zap, ChevronRight, Shield, Clock, MapPin, Moon, Sun
} from "lucide-react";
import { useEffect } from "react";

const STEPS = [
  { icon: <BookOpen size={24} />, title: "Build Your Profile", desc: "Upload your resume or manually list your skills. No branch discrimination here." },
  { icon: <Zap size={24} />, title: "AI Skill Verification", desc: "Our AI extracts and classifies your top skills and matches you to the right roles." },
  { icon: <Target size={24} />, title: "Get Matched & Placed", desc: "Companies discover you based on skills. Get shortlisted, interview, and get placed." },
];

const PROBLEMS = [
  "Mechanical engineers rejected from software roles despite strong coding skills",
  "CGPA-based filters blocking talented candidates before interviews",
  "Non-CS branches invisibilized in campus drives",
];

const BENEFITS = [
  { icon: <Shield size={22} />, title: "Pre-screened Candidates", desc: "Every student profile is skill-verified before you see it. No more unqualified applications." },
  { icon: <CheckCircle size={22} />, title: "Skill-Verified Profiles", desc: "AI-extracted skills from resumes mean you know exactly what each candidate can do." },
  { icon: <Clock size={22} />, title: "Reduced Time-to-Hire", desc: "Smart skill-based filters let you shortlist the right candidates in minutes, not weeks." },
  { icon: <MapPin size={22} />, title: "BITS Pilani Talent", desc: "Direct access to top engineering talent from BITS Pilani's Pilani, Goa, and Hyderabad campuses." },
];

const FEATURES = [
  { icon: <Star size={20} />, title: "Skill-First Matching", desc: "Get discovered by companies based on what you know, not what your degree says." },
  { icon: <Users size={20} />, title: "All Branches Welcome", desc: "Mechanical, Civil, EEE, Chemical — every branch has opportunities here." },
  { icon: <TrendingUp size={20} />, title: "Placement Score", desc: "See your placement probability score and know exactly how to improve it." },
  { icon: <Building2 size={20} />, title: "Top Companies", desc: "Companies actively seeking skill-verified talent from BITS Pilani." },
  { icon: <CheckCircle size={20} />, title: "AI-Powered Resume", desc: "Our AI parses your resume and extracts skills automatically." },
  { icon: <Target size={20} />, title: "Real-time Tracking", desc: "Track your application status in real-time, from pending to placed." },
];

export default function LandingPage() {
  const { user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();


  useEffect(() => {
    if (user?.role === "student") navigate("/student/dashboard");
    else if (user?.role === "admin") navigate("/recruiter/dashboard");
  }, [user, navigate]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("is-visible"); }),
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    const els = document.querySelectorAll(".scroll-reveal, .hero-reveal");
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  }, []);

  const bg = isDark ? '#0a0a0f' : '#ffffff';
  const sectionAlt = isDark ? 'rgba(255,255,255,0.02)' : '#f8f9fa';
  const textPrimary = isDark ? '#ffffff' : '#0a0a0f';
  const textSecondary = isDark ? 'rgba(255,255,255,0.6)' : '#64748b';
  const textMuted = isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const navBg = isDark ? 'rgba(10,10,15,0.88)' : 'rgba(255,255,255,0.92)';
  const navBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)';
  const navShadow = isDark ? '0 4px 30px rgba(0,0,0,0.4)' : '0 4px 30px rgba(0,0,0,0.08)';
  const cardBg = isDark ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)';
  const cardShadow = isDark ? 'none' : '0 1px 8px rgba(0,0,0,0.05)';
  const cardStyle = { background: cardBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: cardBorder, boxShadow: cardShadow };
  const sectionBorderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const accent = '#059669';
  const accentDark = '#047857';
  const accentLight = isDark ? 'rgba(5,150,105,0.15)' : 'rgba(5,150,105,0.08)';
  const accentBorder = 'rgba(5,150,105,0.25)';

  return (
    <div style={{ background: bg, minHeight: '100vh' }}>

      {/* Floating Pill Navbar */}
      <div style={{ position: 'sticky', top: '16px', zIndex: 50, padding: '16px 16px 0 16px', display: 'flex', justifyContent: 'center' }}>
        <nav className={isDark ? 'glass-dark' : 'glass'} style={{ width: '100%', maxWidth: '940px', background: isDark ? 'rgba(10,10,15,0.55)' : 'rgba(255,255,255,0.55)', backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)', border: navBorder, borderRadius: '9999px', boxShadow: isDark ? '0 4px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)' : '0 4px 40px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.8)', padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: `linear-gradient(135deg, ${accent}, ${accentDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: 'white', fontWeight: 'bold', fontSize: '12px' }}>W</span>
            </div>
            <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: '16px', color: textPrimary, whiteSpace: 'nowrap' }}>WonStepCareer</span>
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div className="hidden md:flex items-center">
              <a href="#about" style={{ fontSize: '13px', color: textSecondary, padding: '6px 12px', borderRadius: '9999px', textDecoration: 'none' }}>About Us</a>
              <a href="#how-it-works" style={{ fontSize: '13px', color: textSecondary, padding: '6px 12px', borderRadius: '9999px', textDecoration: 'none' }}>How It Works</a>
              <a href="#for-companies" style={{ fontSize: '13px', color: textSecondary, padding: '6px 12px', borderRadius: '9999px', textDecoration: 'none' }}>For Companies</a>
            </div>
            <Link to="/student/login" data-testid="nav-student-login-btn">
              <Button variant="ghost" size="sm" style={{ fontSize: '13px', borderRadius: '9999px', color: textSecondary }}>Login</Button>
            </Link>
            <Link to="/student/signup" data-testid="nav-signup-btn">
              <Button size="sm" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white', borderRadius: '9999px', fontSize: '13px', fontWeight: 600, boxShadow: `0 2px 12px rgba(5,150,105,0.3)` }}>
                Sign Up Free
              </Button>
            </Link>
            <button
              onClick={e => toggleTheme(e)}
              aria-label="Toggle theme"
              style={{ width: '32px', height: '32px', borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textSecondary, flexShrink: 0, marginLeft: '4px' }}
            >
              {isDark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
          </div>
        </nav>
      </div>

      {/* Hero */}
      <section style={{ paddingTop: '64px', paddingBottom: '88px', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: isDark
            ? 'radial-gradient(ellipse 1000px 500px at 50% -5%, rgba(5,150,105,0.14) 0%, transparent 70%)'
            : 'radial-gradient(ellipse 1000px 500px at 50% -5%, rgba(5,150,105,0.07) 0%, transparent 70%)'
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="hero-reveal" style={{ transitionDelay: '0ms' }}>
              <Badge
                className="mb-6 text-xs tracking-widest uppercase font-semibold border-0 inline-flex"
                style={{ background: accentLight, color: accent }}
                data-testid="hero-badge"
              >
                Skill-Based Placement Platform for BITS Pilani
              </Badge>
            </div>
            <h1
              className="hero-reveal text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary, transitionDelay: '150ms' }}
            >
              Get placed based on your{" "}
              <span style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                skills
              </span>
              ,<br />not your branch.
            </h1>
            <p
              className="hero-reveal text-lg mb-10 leading-relaxed"
              style={{ color: textSecondary, transitionDelay: '300ms' }}
            >
              WonStepCareer connects engineering students from all branches to companies that value what you can do — not just your department or CGPA.
            </p>
            <div
              className="hero-reveal flex flex-col sm:flex-row gap-4 justify-center"
              style={{ transitionDelay: '450ms' }}
            >
              <Link to="/student/signup" data-testid="hero-student-cta">
                <Button size="lg" className="gap-2 w-full sm:w-auto font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white', boxShadow: `0 4px 20px rgba(5,150,105,0.35)` }}>
                  I'm a Student <ArrowRight size={18} />
                </Button>
              </Link>
              <Link to="/recruiter/login" data-testid="hero-recruiter-cta">
                <Button size="lg" variant="outline" className="gap-2 w-full sm:w-auto" style={{ borderColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(5,150,105,0.4)', color: isDark ? 'white' : accent, background: 'transparent' }}>
                  I'm a Recruiter <ChevronRight size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-20" style={{ background: isDark ? 'rgba(5,150,105,0.04)' : 'rgba(5,150,105,0.03)', borderTop: `1px solid ${accentBorder}`, borderBottom: `1px solid ${accentBorder}` }} data-testid="about-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="scroll-reveal text-xs tracking-[0.2em] uppercase font-semibold block mb-3" style={{ color: accent }}>About Us</span>
            <h2 className="scroll-reveal text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary, transitionDelay: '60ms' }}>
              Built at BITS. Built for BITS.
            </h2>
          </div>
          <div
            className="scroll-reveal rounded-2xl p-8 sm:p-10 space-y-5"
            style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(5,150,105,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(5,150,105,0.12)'}`, transitionDelay: '120ms' }}
          >
            <p className="text-base leading-relaxed" style={{ color: textSecondary }}>
              WonStepCareer is built by a BITS Pilani student who watched and still watches batchmates with real skills getting overlooked — not because they weren't good enough, but because resumes don't tell the full story.
            </p>
            <p className="text-base leading-relaxed" style={{ color: textSecondary }}>
              Campus recruitment today is broken in a simple way: companies receive hundreds of applications and have no reliable way to tell who can actually do the work. Students spend weeks preparing for drives they're poorly matched for. Everyone wastes time.
            </p>
            <div className="border-l-2 pl-5 py-1" style={{ borderColor: accent }}>
              <p className="text-base leading-relaxed font-medium" style={{ color: textPrimary }}>
                WSC fixes the mismatch. Students build a verified skill profile — not just a resume, but proof of what they know. Companies get a pre-screened, skill-matched shortlist. The result is faster hiring, better matches, and a process that rewards actual ability over presentation.
              </p>
            </div>
            <p className="text-base leading-relaxed" style={{ color: textSecondary }}>
              We're starting with BITS Pilani because we know this campus. We know the recruiters who come here, the skills they're looking for, and the gap between what students can do and what their resumes show. This is the pilot. The model scales.
            </p>
          </div>
        </div>
      </section>

      {/* By The Numbers */}
      <section
        style={{ borderTop: `1px solid ${sectionBorderColor}`, borderBottom: `1px solid ${sectionBorderColor}`, background: sectionAlt }}
        data-testid="stats-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-center mb-10">
            <span className="scroll-reveal text-xs tracking-[0.2em] uppercase font-semibold block mb-3" style={{ color: accent }}>By The Numbers</span>
            <h2 className="scroll-reveal text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary, transitionDelay: '60ms' }}>
              The latest BITS Pilani placement statistics
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { num: "350+",        label: "Companies visit BITS Pilani annually" },
              { num: "80-90%",      label: "Average BITS placement rate" },
              { num: "₹31.63 LPA",  label: "Average BITS Pilani package, Batch 2025 (early data, Semester 1)" },
              { num: "75%",         label: "Resumes never reach a human reviewer" },
            ].map((s, i) => (
              <div
                key={i}
                className="scroll-reveal p-6 rounded-2xl text-center glass-card"
                style={{ ...cardStyle, transitionDelay: `${i * 80}ms` }}
              >
                <div className="text-2xl sm:text-3xl font-bold mb-1.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: accent }}>{s.num}</div>
                <div className="text-sm leading-snug" style={{ color: textSecondary }}>{s.label}</div>
              </div>
            ))}
          </div>
          <p className="text-center mt-6 text-xs" style={{ color: textMuted }}>
            *Sources: BITS Pilani Placement Division Reports, Mercer Mettl India Graduate Skills Index 2025, NASSCOM Talent Report 2025
          </p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="scroll-reveal text-xs tracking-[0.2em] uppercase font-semibold block mb-3" style={{ color: accent }}>The Problem</span>
              <h2 className="scroll-reveal text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary, transitionDelay: '60ms' }}>
                Branch bias is killing careers before they start
              </h2>
              <p className="scroll-reveal mb-8 leading-relaxed" style={{ color: textSecondary, transitionDelay: '120ms' }}>
                Every year, thousands of engineering students lose out on great opportunities purely because of their department — not their abilities. Companies filter out talent before giving it a chance.
              </p>
              <div className="space-y-4">
                {PROBLEMS.map((p, i) => (
                  <div
                    key={i}
                    className="scroll-reveal flex items-start gap-3 p-4 rounded-xl"
                    style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', transitionDelay: `${(i + 3) * 60}ms` }}
                  >
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(239,68,68,0.15)' }}>
                      <span className="text-red-400 text-xs font-bold">✗</span>
                    </div>
                    <p className="text-sm" style={{ color: textSecondary }}>{p}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="scroll-reveal rounded-2xl p-10 text-center" style={{ ...cardStyle }}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: accentLight }}>
                <Users size={32} style={{ color: accent }} />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>Branch bias is real</h3>
              <p className="leading-relaxed" style={{ color: textSecondary }}>Non-CS engineers with strong skills deserve a fair shot. WonStepCareer is changing that.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20" style={{ background: sectionAlt }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="scroll-reveal text-xs tracking-[0.2em] uppercase font-semibold block mb-3" style={{ color: accent }}>How It Works</span>
            <h2 className="scroll-reveal text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary, transitionDelay: '60ms' }}>
              Three steps to your dream job
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((step, i) => (
              <div
                key={i}
                className="scroll-reveal relative p-8 rounded-2xl hover:-translate-y-1 transition-all duration-200 glass-card"
                style={{ ...cardStyle, transitionDelay: `${i * 100}ms` }}
              >
                <div className="absolute -top-4 left-8 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})` }}>
                  {i + 1}
                </div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 mt-2" style={{ background: accentLight, color: accent }}>
                  {step.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: textSecondary }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Companies */}
      <section
        id="for-companies"
        className="py-16"
        style={{ background: isDark ? 'rgba(5,150,105,0.04)' : 'rgba(5,150,105,0.03)', borderTop: `1px solid ${accentBorder}`, borderBottom: `1px solid ${accentBorder}` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="scroll-reveal text-xs tracking-[0.2em] uppercase font-semibold block mb-3" style={{ color: accent }}>For Recruiters</span>
            <h2 className="scroll-reveal text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary, transitionDelay: '60ms' }}>
              Hire smarter with WonStepCareer
            </h2>
            <p className="scroll-reveal mt-3 text-sm" style={{ color: textMuted, transitionDelay: '120ms' }}>
              Companies coming soon — pilot launching at BITS Pilani 2026
            </p>
          </div>
          {/* The Hiring Problem stats */}
          <div className="mb-14">
            <div className="text-center mb-8">
              <span className="scroll-reveal text-xs tracking-[0.2em] uppercase font-semibold block mb-2" style={{ color: accent }}>The Data</span>
              <h3 className="scroll-reveal text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary, transitionDelay: '60ms' }}>
                Why traditional campus hiring is broken
              </h3>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { num: "₹3.73 Lakh",  label: "Average cost per successful campus hire (traditional process)" },
                { num: "75%",          label: "Resumes auto-rejected before human review" },
                { num: "56%",          label: "Indian hiring managers detected resume fraud in their last cycle (NASSCOM 2024)" },
                { num: "3–5 Rounds",   label: "Average interview rounds needed in Indian campus placement" },
                { num: "Only 42.6%",   label: "Indian graduates deemed employable (Mercer Mettl 2025)" },
                { num: "2–6 Weeks",    label: "Time wasted per campus hiring cycle" },
                { num: "88%",          label: "Applications found to be AI-generated noise — a single Pune startup received 3,400 overnight, 88% identical" },
                { num: "₹1.8 Lakh",   label: "Cost just for Day 0 campus slot access at a premium institute — before a single interview is conducted" },
                { num: "3×",           label: "Salary gap between CSE and Mechanical at the same IIT (IIT Madras: ₹46 LPA vs ₹15 LPA)" },
              ].map((s, i) => (
                <div
                  key={i}
                  className="scroll-reveal p-5 rounded-2xl glass-card"
                  style={{ ...cardStyle, transitionDelay: `${i * 70}ms` }}
                >
                  <div className="text-2xl sm:text-3xl font-bold mb-1.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: accent }}>{s.num}</div>
                  <div className="text-sm leading-snug" style={{ color: textSecondary }}>{s.label}</div>
                </div>
              ))}
            </div>
            <p className="text-center mt-5 text-xs" style={{ color: textMuted }}>
              *Sources: Mercer Mettl India Graduate Skills Index 2025, Aspiring Minds Longitudinal Study, NASSCOM Talent Integrity Survey 2024, BITS Pilani Placement Division Reports
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((b, i) => (
              <div
                key={i}
                className="scroll-reveal p-6 rounded-xl"
                style={{ ...cardStyle, transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: accentLight, color: accent }}>
                  {b.icon}
                </div>
                <h3 className="font-semibold mb-2" style={{ color: textPrimary }}>{b.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: textSecondary }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="scroll-reveal text-xs tracking-[0.2em] uppercase font-semibold block mb-3" style={{ color: accent }}>Why WonStepCareer</span>
            <h2 className="scroll-reveal text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary, transitionDelay: '60ms' }}>
              Built for every engineer
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((b, i) => (
              <div
                key={i}
                className="scroll-reveal p-6 rounded-xl hover:-translate-y-0.5 transition-all duration-200 glass-card"
                style={{ ...cardStyle, transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: accentLight, color: accent }}>
                  {b.icon}
                </div>
                <h3 className="font-semibold mb-2" style={{ color: textPrimary }}>{b.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: textSecondary }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ background: isDark ? 'rgba(5,150,105,0.05)' : 'rgba(5,150,105,0.04)', borderTop: `1px solid ${accentBorder}` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="scroll-reveal text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: textPrimary }}>
            Ready to get placed on your terms?
          </h2>
          <p className="scroll-reveal mb-8 text-lg" style={{ color: textSecondary, transitionDelay: '60ms' }}>
            Join students who are building skill-verified profiles and landing their dream jobs.
          </p>
          <div className="scroll-reveal flex flex-col sm:flex-row gap-4 justify-center" style={{ transitionDelay: '120ms' }}>
            <Link to="/student/signup" data-testid="bottom-student-cta">
              <Button size="lg" className="gap-2 font-semibold" style={{ background: `linear-gradient(135deg, ${accent}, ${accentDark})`, color: 'white', boxShadow: `0 4px 20px rgba(5,150,105,0.35)` }}>
                Get Started Free <ArrowRight size={18} />
              </Button>
            </Link>
            <Link to="/recruiter/login" data-testid="bottom-recruiter-cta">
              <Button size="lg" variant="outline" className="gap-2" style={{ borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(5,150,105,0.35)', color: isDark ? 'white' : accent, background: 'transparent' }}>
                Hire on Skills <ChevronRight size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${sectionBorderColor}`, padding: '32px 0', background: sectionAlt }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: `linear-gradient(135deg, ${accent}, ${accentDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontWeight: 'bold', fontSize: '10px' }}>W</span>
            </div>
            <span className="font-semibold" style={{ color: textPrimary }}>WonStepCareer</span>
          </div>
          <p className="text-sm" style={{ color: textMuted }}>© 2026 WonStepCareer. Empowering engineers at BITS Pilani.</p>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Link to="/student/login" style={{ fontSize: '14px', color: textMuted, textDecoration: 'none' }}>Students</Link>
            <Link to="/recruiter/login" style={{ fontSize: '14px', color: textMuted, textDecoration: 'none' }}>Recruiters</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
